/**
 * This component will automatically use the fallback editor in browsers where
 * CMLEditor, which uses Slate under the hood, is not supported or works poorly.
 * Currently, Slate is not supported in IE. (see https://docs.slatejs.org/general/faq)
 *
 * For more info about CML, see https://coursera.atlassian.net/wiki/spaces/EN/pages/48267760/Frontend+-+CML
 */
import * as React from 'react';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import isMobileApp from 'js/lib/isMobileApp';
import UserAgent from 'js/lib/useragent';

import CMLEditor from 'bundles/authoring/content-authoring/components/CMLEditor';
import type { Props as CMLEditorProps } from 'bundles/authoring/content-authoring/components/CMLEditor';
import { FallbackEditor } from 'bundles/cml';

type Props = {} & CMLEditorProps;

function isUserAgentCMLSupported(userAgent: UserAgent) {
  const isIE = userAgent.browser.name === 'IE';
  // excluding android here because android on web is not able to type into the CML editor
  // related Jira: https://coursera.atlassian.net/browse/LP-5167?atlOrigin=eyJpIjoiZmQwOWNlN2MzODYxNGViZThkMGZjYTdiM2RjZjc1OTgiLCJwIjoiaiJ9
  const supported = !(isIE || userAgent.isAndroid || isMobileApp.get());
  return supported;
}

export function CMLEditorOrFallbackEditor(props: Props) {
  const { contentId, initialCML, placeholder, onContentChange, ariaLabel, ariaDescribedBy } = props;
  const userAgent = new UserAgent(navigator?.userAgent);

  let editor;

  if (isUserAgentCMLSupported(userAgent)) {
    editor = <CMLEditor {...props} />;
  } else {
    editor = (
      <FallbackEditor
        id={contentId}
        cml={initialCML}
        placeholder={placeholder}
        onChange={onContentChange}
        ariaLabel={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
        isFocused={props.focusOnLoad}
      />
    );
  }

  return editor;
}

CMLEditorOrFallbackEditor.displayName = 'CMLEditorOrFallbackEditor';

export default deferToClientSideRender(CMLEditorOrFallbackEditor);
